import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { MagnifyingGlass, Heart, Funnel, Plus, Tag, MapPin } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'clubs', label: 'Clubs' },
  { key: 'bags', label: 'Bags' },
  { key: 'balls', label: 'Balls' },
  { key: 'apparel', label: 'Apparel' },
  { key: 'accessories', label: 'Accessories' },
  { key: 'cart-tech', label: 'Cart & Tech' },
];

const SORT_OPTIONS = [
  { key: 'newest', label: 'Newest' },
  { key: 'price_low', label: 'Price: Low' },
  { key: 'price_high', label: 'Price: High' },
];

function ListingCard({ listing, onSave, userId }) {
  const navigate = useNavigate();
  const firstPhoto = listing.photos?.[0];
  const isSaved = (listing.saved_by || []).includes(userId);

  return (
    <div
      className="border border-border bg-card overflow-hidden group cursor-pointer hover:border-primary/30 transition-all duration-300"
      onClick={() => navigate(`/shop/${listing.listing_id}`)}
      data-testid={`listing-card-${listing.listing_id}`}
    >
      <div className="aspect-square relative bg-muted overflow-hidden">
        {firstPhoto ? (
          <img src={`${API_BASE}/api/media/${firstPhoto}`} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Tag size={32} weight="thin" className="text-muted-foreground" />
          </div>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onSave(listing.listing_id); }}
          className="absolute top-2 right-2 w-8 h-8 bg-white/80 backdrop-blur rounded-full flex items-center justify-center transition-colors hover:bg-white"
          data-testid={`save-listing-${listing.listing_id}`}
        >
          <Heart size={16} weight={isSaved ? 'fill' : 'regular'} className={isSaved ? 'text-red-500' : 'text-foreground'} />
        </button>
        <div className="absolute bottom-2 left-2">
          <span className="px-2 py-0.5 bg-black/60 backdrop-blur text-white text-[10px] font-sans uppercase tracking-wide rounded-sm">
            {listing.condition}
          </span>
        </div>
      </div>
      <div className="p-3">
        <p className="text-lg font-serif font-semibold text-primary">${listing.price?.toFixed(0)}</p>
        <p className="text-sm font-sans text-foreground truncate mt-0.5">{listing.title}</p>
        {listing.location && (
          <p className="text-xs text-muted-foreground font-sans mt-1 flex items-center gap-1">
            <MapPin size={10} /> {listing.location}
          </p>
        )}
      </div>
    </div>
  );
}

export default function MarketplacePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') params.set('category', category);
      if (search) params.set('search', search);
      params.set('sort', sortBy);
      params.set('limit', '20');
      const { data } = await api.get(`/listings?${params}`);
      setListings(data.listings);
      setTotal(data.total);
    } catch {} finally { setLoading(false); }
  }, [category, sortBy, search]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleSave = async (listingId) => {
    if (!user) { navigate('/auth'); return; }
    try {
      const { data } = await api.post(`/listings/${listingId}/save`);
      setListings(prev => prev.map(l => {
        if (l.listing_id !== listingId) return l;
        return {
          ...l,
          saved_by: data.saved
            ? [...(l.saved_by || []), user.user_id]
            : (l.saved_by || []).filter(id => id !== user.user_id)
        };
      }));
    } catch {}
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background" data-testid="marketplace-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-foreground font-medium tracking-tight" data-testid="marketplace-title">Shop</h1>
            <p className="text-muted-foreground text-sm mt-1 font-sans">{total} listing{total !== 1 ? 's' : ''}</p>
          </div>
          {user && (
            <button
              onClick={() => navigate('/shop/new')}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-sm text-xs font-semibold uppercase tracking-wide font-sans hover:bg-[#083313] transition-colors"
              data-testid="create-listing-btn"
            >
              <Plus size={14} weight="bold" /> Sell
            </button>
          )}
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="mt-6 flex gap-2" data-testid="marketplace-search-form">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search listings..."
              className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 pl-7 pr-2 py-2 text-sm font-sans"
              data-testid="marketplace-search-input"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-primary text-primary-foreground rounded-sm text-xs font-semibold uppercase tracking-wide font-sans" data-testid="marketplace-search-btn">
            Search
          </button>
        </form>

        {/* Categories */}
        <div className="flex gap-2 mt-6 overflow-x-auto pb-2 scrollbar-hide" data-testid="category-pills">
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              onClick={() => setCategory(cat.key)}
              className={`px-4 py-1.5 rounded-sm text-xs font-sans font-semibold tracking-wide whitespace-nowrap transition-colors duration-300 ${
                category === cat.key
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border text-foreground hover:border-primary/50'
              }`}
              data-testid={`category-${cat.key}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2 mt-4">
          <Funnel size={14} className="text-muted-foreground" />
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => setSortBy(opt.key)}
              className={`text-xs font-sans transition-colors ${sortBy === opt.key ? 'text-primary font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`sort-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
            {[...Array(6)].map((_, i) => <div key={i} className="aspect-square bg-muted animate-pulse" />)}
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6" data-testid="listings-grid">
            {listings.map(l => <ListingCard key={l.listing_id} listing={l} onSave={handleSave} userId={user?.user_id} />)}
          </div>
        ) : (
          <div className="text-center py-16" data-testid="empty-marketplace">
            <Tag size={36} weight="thin" className="mx-auto text-muted-foreground" />
            <p className="text-muted-foreground text-sm mt-3 font-sans">No listings found</p>
            {user && (
              <button onClick={() => navigate('/shop/new')} className="mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-sm text-xs font-semibold uppercase font-sans" data-testid="empty-marketplace-sell-btn">
                Be the First to Sell
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
